// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import DOMPurify from "dompurify";
import { EColorScheme, MIN_RENDER_WIDTH_PX, WIDTH_BUCKET_PX } from "../types";
import { chartSize } from "./chartSize";
// A chart is drawn in `<iframe sandbox="" srcDoc>`: no script runs, and this
// policy, first in the head, lets the document load nothing but inline styles
// and data: fonts and images. The server sanitizes the SVG too; cleaning it
// again here keeps the frame safe whichever renderer produced it.
export var CHART_CSP = "default-src 'none'; style-src 'unsafe-inline'; font-src data:; img-src data:";
var HREF_ATTRIBUTES = new Set(["href", "xlink:href"]);
var IMPORT_RULE = /@import[^;]*(;|$)/gi;
/** `url(...)` whose target, quoted or not, is not a `#fragment` of the document. */
var OUTSIDE_URL = /url\(\s*(?!['"]?\s*#)[^)]*\)?/gi;
/**
 * CSS the chart may keep: no `@import`, and `url()` only to the document's own
 * gradients, patterns and filters. A CSS escape could spell `url` or `@import`
 * so that no pattern sees it, so styles with one are dropped whole.
 */
export function sanitizeCss(css) {
    if (css.includes("\\"))
        return "";
    return css.replace(IMPORT_RULE, "").replace(OUTSIDE_URL, "none");
}
var purifier;
function svgPurifier() {
    if (purifier)
        return purifier;
    // An instance of its own, so that its hook applies to charts only.
    var instance = DOMPurify(window);
    instance.addHook("uponSanitizeAttribute", function (_node, data) {
        if (HREF_ATTRIBUTES.has(data.attrName) && !data.attrValue.trim().startsWith("#")) {
            data.keepAttr = false;
        }
        if (data.attrName === "style")
            data.attrValue = sanitizeCss(data.attrValue);
    });
    instance.addHook("uponSanitizeElement", function (node, data) {
        if (data.tagName === "style" && node.textContent) {
            node.textContent = sanitizeCss(node.textContent);
        }
    });
    purifier = instance;
    return instance;
}
export function sanitizeSvg(svg) {
    return svgPurifier().sanitize(svg, {
        USE_PROFILES: { svg: true, svgFilters: true },
        // Reused glyphs and markers; the hook keeps only in-document references.
        ADD_TAGS: ["use"],
        FORBID_TAGS: ["script", "meta", "link", "foreignObject", "a", "iframe"],
        FORBID_ATTR: ["srcset", "ping"],
    });
}
/** FNV-1a: cheap, stable, enough to tell two documents apart. */
export function hashString(text) {
    var hash = 0x811c9dc5;
    for (var index = 0; index < text.length; index++) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}
/**
 * The width a render is requested at: whole 40 px steps, never below
 * {@link MIN_RENDER_WIDTH_PX}. A frame narrower than that shrinks the chart
 * to fit (`max-width: 100%; height: auto`).
 */
export function widthBucket(width) {
    var steps = Math.floor(Math.max(0, width) / WIDTH_BUCKET_PX);
    return Math.max(MIN_RENDER_WIDTH_PX, steps * WIDTH_BUCKET_PX);
}
/**
 * The width an editor preview is drawn at: the card's own, when the editor
 * was opened from one, so the preview draws what the dashboard does; else
 * the preview pane's. Bucketed like a card's, never below
 * {@link MIN_RENDER_WIDTH_PX}.
 */
export function previewWidth(cardWidth, paneWidth) {
    var _a;
    return widthBucket((_a = cardWidth !== null && cardWidth !== void 0 ? cardWidth : paneWidth) !== null && _a !== void 0 ? _a : 0);
}
export function chartDocument(_a) {
    var svg = _a.svg, colorScheme = _a.colorScheme, _b = _a.fontCss, fontCss = _b === void 0 ? "" : _b;
    var clean = sanitizeSvg(svg);
    var scheme = colorScheme === EColorScheme.DARK ? "dark" : "light";
    var html = "<!doctype html><html><head>" +
        "<meta http-equiv=\"Content-Security-Policy\" content=\"".concat(CHART_CSP, "\">") +
        '<meta charset="utf-8">' +
        "<style>" +
        fontCss +
        ":root { color-scheme: ".concat(scheme, "; }") +
        "html, body { margin: 0; height: 100%; background: transparent; }" +
        "body { overflow-y: auto; overflow-x: hidden; }" +
        "svg { display: block; max-width: 100%; height: auto; }" +
        "</style></head><body>" +
        clean +
        "</body></html>";
    return { html: html, hash: hashString(html), size: chartSize(clean) };
}
